<template>
  <!-- Grows to fill its container (both usages are right drawers), so the scope list uses
       the full height instead of a 320px box with dead space beneath it. -->
  <div class="flex flex-col gap-2 flex-1 min-h-0">
    <div class="flex items-center justify-between">
      <p class="text-sm font-medium">Scope</p>
      <div class="flex items-center gap-3 text-xs">
        <span class="text-muted-foreground tabular-nums">{{ selectedIds.length }} of {{ options.length }}</span>
        <button type="button" class="font-medium text-primary hover:underline" @click="selectAll">Select all</button>
        <button type="button" class="font-medium text-muted-foreground hover:underline" @click="clear">Clear</button>
      </div>
    </div>

    <div class="rounded-lg border border-border divide-y divide-border flex-1 min-h-0 overflow-y-auto">
      <div v-for="group in groups" :key="group.key">
        <div class="flex items-center justify-between px-3 py-1.5 bg-muted sticky top-0 z-10">
          <p class="text-xs font-semibold uppercase tracking-wide text-foreground">{{ group.label }}</p>
          <button type="button" class="text-xs font-medium text-primary hover:underline" @click="toggleGroup(group)">
            {{ allInGroupSelected(group) ? 'Clear' : 'Select all' }}
          </button>
        </div>
        <label
          v-for="opt in group.options"
          :key="opt.id"
          class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/40"
        >
          <Checkbox :model-value="isSelected(opt)" @update:model-value="(v) => toggle(opt, v)" />
          <span class="truncate">{{ humanize(opt.text || opt.id) }}</span>
          <span class="ml-auto shrink-0 text-xs italic font-mono text-muted-foreground">{{ opt.id }}</span>
          <!-- Opens the example payload. stopPropagation so it does not also toggle the checkbox. -->
          <button
            v-if="hasExample(opt.id)"
            type="button"
            class="shrink-0 text-muted-foreground hover:text-foreground"
            :aria-label="`Example payload for ${opt.id}`"
            @click.stop.prevent="openExample(opt.id)"
          >
            <Info class="size-3.5" />
          </button>
        </label>
      </div>
    </div>

    <!-- Example payload for the event whose info icon was clicked. -->
    <AlertDialog :open="exampleEvent !== null" @update:open="(o) => !o && (exampleEvent = null)">
      <AlertDialogContent class="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{{ exampleEvent }}</AlertDialogTitle>
          <AlertDialogDescription>
            Example payload delivered to your endpoint for this event. Amounts are in minor units.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div class="max-h-[60vh] overflow-auto rounded-lg bg-muted px-3 py-2 text-xs">
          <vue-json-pretty :data="examplePayload" style="font-size: 11px" />
        </div>
        <AlertDialogFooter>
          <AlertDialogAction @click="exampleEvent = null">Close</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<script>
import { Checkbox } from '@boogi/ui'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@boogi/ui'
import { Info } from 'lucide-vue-next'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'
import { exampleFor } from '@/constants/webhookExamples'

export default {
  name: 'WebhookScopeSelect',
  components: {
    Checkbox,
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Info,
    VueJsonPretty,
  },
  props: {
    // Array of selected option objects ({ id, text }).
    modelValue: { type: Array, default: () => [] },
    // Array of all available option objects ({ id, text }).
    options: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      // Event type whose example is open, or null when the dialog is closed.
      exampleEvent: null,
    }
  },
  computed: {
    examplePayload() {
      return this.exampleEvent ? exampleFor(this.exampleEvent) : null
    },
    selectedIds() {
      return this.modelValue.map((o) => o.id)
    },
    groups() {
      const buckets = [
        { key: 'cashback', label: 'Cashback', match: (id) => id.startsWith('CASHBACK_') },
        { key: 'recommendations', label: 'Recommendations', match: (id) => id.startsWith('RECOMMENDATION_') },
        { key: 'offers', label: 'Offers', match: (id) => id.startsWith('OFFER_') || id.startsWith('CLICK_TO_ACTIVATE') },
        { key: 'tiers', label: 'Tiers & users', match: (id) => id.startsWith('TIER_') || id.startsWith('USER_') },
        { key: 'giftCards', label: 'Gift cards', match: (id) => id.startsWith('GIFT_CARD_') },
      ]
      const result = buckets.map((b) => ({ ...b, options: this.options.filter((o) => b.match(o.id)) }))
      // Anything that didn't match a bucket goes into "Other".
      const matched = new Set(result.flatMap((g) => g.options.map((o) => o.id)))
      const other = this.options.filter((o) => !matched.has(o.id))
      if (other.length) result.push({ key: 'other', label: 'Other', options: other })
      return result.filter((g) => g.options.length)
    },
  },
  methods: {
    /** True when an example payload exists for this event. */
    hasExample(eventType) {
      return exampleFor(eventType) !== null
    },

    openExample(eventType) {
      this.exampleEvent = eventType
    },

    humanize(value) {
      return value
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    isSelected(opt) {
      return this.selectedIds.includes(opt.id)
    },
    toggle(opt, checked) {
      const next = checked
        ? [...this.modelValue, opt]
        : this.modelValue.filter((o) => o.id !== opt.id)
      this.$emit('update:modelValue', next)
    },
    allInGroupSelected(group) {
      return group.options.every((o) => this.isSelected(o))
    },
    toggleGroup(group) {
      if (this.allInGroupSelected(group)) {
        const ids = new Set(group.options.map((o) => o.id))
        this.$emit('update:modelValue', this.modelValue.filter((o) => !ids.has(o.id)))
      } else {
        const current = new Set(this.selectedIds)
        const additions = group.options.filter((o) => !current.has(o.id))
        this.$emit('update:modelValue', [...this.modelValue, ...additions])
      }
    },
    selectAll() {
      this.$emit('update:modelValue', [...this.options])
    },
    clear() {
      this.$emit('update:modelValue', [])
    },
  },
}
</script>
